import {
  useCancelIotJobExecutionMutation,
  useGetIotJobStatusQuery,
} from '../../gql/generated';
import CondorSpinner from '../CondorSpinner';
import React, { useEffect, useMemo, useState } from 'react';
import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import Tooltip from '@mui/material/Tooltip';
import { useRoleAuthorization } from '../auth/RoleAuthorization';
import { OnSubmitModal } from '../../Pages/OnSubmitModal';
import { useFormattedDate } from './useFormattedDate';

interface IoTJobDetailsProps {
  canopyDeviceId: string;
  jobId: string;
}

export const IotJobDetails = (props: IoTJobDetailsProps) => {
  const { canopyDeviceId, jobId } = props;

  const { formattedDate } = useFormattedDate();
  const { data, loading, error, refetch } = useGetIotJobStatusQuery({
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        canopyDeviceId,
        jobId,
      },
    },
  });
  const [isButtonVisible, setIsButtonVisible] = useState<boolean>(false);
  const { canCancelJob } = useRoleAuthorization();
  const [modalState, setModalState] = useState({
    isVisible: false,
    title: '',
    message: '',
  });
  const iotJobStatus = useMemo(() => {
    const jobStatus = data?.getIotJobStatus;
    return jobStatus
      ? {
          status: jobStatus.status,
          latestUpdatedAt: jobStatus.lastUpdatedAt,
          isCancellable: jobStatus.isCancellable,
        }
      : {
          status: 'unknown',
          latestUpdatedAt: 'unknown',
        };
  }, [data]);

  useEffect(() => {
    if (iotJobStatus.isCancellable && canCancelJob()) {
      setIsButtonVisible(true);
    }
  }, [data, canCancelJob]);

  const [
    cancelIotJobExecution,
    { error: cancelIotJobError, loading: cancelIotJobLoading },
  ] = useCancelIotJobExecutionMutation();

  const handleCancelIotJobExecution = async () => {
    const response = await cancelIotJobExecution({
      variables: {
        input: {
          canopyDeviceId,
          jobId,
        },
      },
    });
    setModalState({
      isVisible: true,
      title: cancelIotJobError ? 'Error' : 'Success',
      message: response.data?.cancelIotJobExecution ?? 'Cancel Success',
    });
    await refetch();
    setIsButtonVisible(false);
  };
  const lastUpdatedAt = (): string => {
    const processedDate = iotJobStatus
      ? formattedDate(iotJobStatus.latestUpdatedAt)
      : null;
    return processedDate
      ? `Last updated at: ${processedDate}`
      : 'Not known when status was updated';
  };
  const handleCloseModal = async () => {
    setModalState({ isVisible: false, title: '', message: '' });
    await refetch();
    setIsButtonVisible(false);
  };

  return (
    <>
      {loading ? (
        <CondorSpinner />
      ) : (
        <div style={{ display: 'flex' }}>
          <div style={{ flexBasis: '40%' }}>
            <p style={{ fontSize: '1em', fontWeight: 'bolder' }}>
              Latest Iot Job Status
            </p>
          </div>
          {error ? (
            <div style={{ display: 'flex' }}>
              <div style={{ flexBasis: '20%' }}></div>
              <div
                style={{
                  flexBasis: '80%',
                  paddingLeft: '20px',
                  color: 'red',
                  fontWeight: 'bold',
                }}
              >
                {error.message}
              </div>
            </div>
          ) : (
            <div style={{ flexBasis: '20%' }}>
              {iotJobStatus && (
                <Tooltip title={lastUpdatedAt()} placement="top">
                  <p style={{ fontSize: '1em' }}>{iotJobStatus.status} </p>
                </Tooltip>
              )}
            </div>
          )}
          <div style={{ flexBasis: '20%' }}>
            {isButtonVisible && (
              <StyledButton
                disabled={cancelIotJobLoading}
                onPress={() => handleCancelIotJobExecution()}
                buttonRules={ButtonRules.primaryA}
              >
                Cancel Job
              </StyledButton>
            )}
          </div>
          <OnSubmitModal
            isVisible={modalState.isVisible}
            onClose={handleCloseModal}
            title={modalState.title}
            message={modalState.message}
          />
        </div>
      )}
    </>
  );
};
